import {FormControl} from 'platform/components';

import {RefObject, useCallback, useEffect} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {
  AccountingDocumentRecalculateApiArg,
  AccountingDocumentRecalculateItemRes,
  useInvoicingDocumentRecalculateMutation,
  useTaxDocumentCalculationV2Mutation,
} from '@dms/api/finance';
import {PaymentType, VatCheckTotalsDefinitionDocumentTypeEnum} from '@dms/api/shared';
import {handleApiError, useGetCurrentBranch} from '@dms/shared';

import {Nullish} from 'shared/web';

import {useAccountingCalculationCache} from '../../../context/useAccountingCalculationCache';
import {AccountingDocumentFormValues} from '../../../types';
import {useGetInvoiceItemsForRecalculation} from '../hooks/useGetInvoiceItemsForRecalculation';
import {WithCorrectiveTaxDocumentArgs} from '../types/WithCorrectiveTaxDocumentArgs';
import {isValidIndex} from '../utils/isValidIndex';
import {useGetInvoiceCurrency} from './useGetInvoiceCurrency';
import {UpdateRowType, useRerenderRows} from './useRerenderRows';

type RecalculateInvoiceItemsArgs = WithCorrectiveTaxDocumentArgs<{
  control: FormControl<AccountingDocumentFormValues>;
  formApi: UseFormReturn<AccountingDocumentFormValues>;
  listItemType: VatCheckTotalsDefinitionDocumentTypeEnum;
  shouldCallOnInitialization?: boolean;
  pricePerUnitEditVersionRef?: RefObject<Record<string, number>>;
  /**
   * Date of taxable supply of the document being corrected. A corrective tax document keeps the
   * VAT rates of the corrected document, so the calculation must resolve rates for this date
   * instead of the corrective document's own date of taxable supply (T20-107267).
   */
  correctedDocumentDateOfTaxableSupply?: string | Nullish;
}>;

type RecalculateSummaryArgs =
  | (Omit<AccountingDocumentRecalculateApiArg, 'items'> & {
      items: AccountingDocumentRecalculateItemRes[];
    })
  | Nullish;

export const useRecalculateInvoiceItems = (props: RecalculateInvoiceItemsArgs) => {
  const {activeBranchId} = useGetCurrentBranch();
  const {data: tenant} = useGetTenantQuery();
  const {taxDocumentCalculationCacheKey, accountingDocumentCalculationCacheKey} =
    useAccountingCalculationCache();

  const [currency] = useGetInvoiceCurrency(props.control, props.listItemType);
  const [mapInvoiceItemsForRecalculation] = useGetInvoiceItemsForRecalculation(
    props.isCorrectiveTaxDocument ?? false,
    currency
  );

  const [calculateTaxDocument] = useInvoicingDocumentRecalculateMutation({
    fixedCacheKey: accountingDocumentCalculationCacheKey,
  });

  const [recalculateSummary] = useTaxDocumentCalculationV2Mutation({
    fixedCacheKey: taxDocumentCalculationCacheKey,
  });

  const {updateDiscount, updateMargin, updateStandardItem, updateAllItems, resetInvalidDiscounts} =
    useRerenderRows(props.formApi, props.pricePerUnitEditVersionRef);

  const recalculateInvoiceSummary = useCallback(
    (recalculateProps: RecalculateSummaryArgs) => {
      if (isNil(recalculateProps)) {
        return;
      }

      const {items, dueDate, ...args} = recalculateProps;

      const invoiceItems = items.map((item) => {
        if (item.type === 'discount') {
          return {
            isUnitPriceWithVat: item.isUnitPriceWithVat,
            quantity: '1',
            reference: item.itemId,
            unitPrice: item.unitPrice.toString() ?? '0',
            vatType: item.vatType,
            relatedItemId: item.relatedItemId ?? undefined,
          };
        }

        return {
          isUnitPriceWithVat: item.isUnitPriceWithVat,
          quantity: item.quantity ?? '0',
          reference: item.itemId,
          unitPrice: item.unitPrice ?? '0',
          vatType: item.vatType,
          relatedItemId: item.relatedItemId ?? undefined,
        };
      });

      return recalculateSummary({
        taxDocumentCalculationRequestBody: {
          ...args,
          taxableSupplyDate: dueDate,
          items: invoiceItems,
          relatedDocumentForCalculation: props.relatedDocumentForCalculation ?? null,
        },
      })
        .unwrap()
        .catch(handleApiError);
    },
    // eslint-disable-next-line react/exhaustive-deps
    [recalculateSummary]
  );

  const recalculate = useCallback(
    /**
     * `currencyOverride` is for callers that know the document's currency before the query
     * `useGetInvoiceCurrency` reads has caught up with it, typically a form field's onChange,
     * which runs before the form holds the new value (T20-142410).
     */
    (itemId?: string, currencyOverride?: string | Nullish) => {
      if (isNil(activeBranchId) || isNil(tenant)) {
        return;
      }

      const {
        invoiceItems,
        dateOfTaxableSupply,
        paymentInfo,
        isExchangeRateRatioEnabled,
        exchangeRateRatio,
      } = props.formApi.getValues();

      const recalculationCurrency = currencyOverride ?? currency;
      const items = mapInvoiceItemsForRecalculation(invoiceItems, recalculationCurrency);
      const hasItemWithNonZeroQuantity = items.some((item) => parseFloat(item.quantity) !== 0);
      // check if all necessary invoice fields are present
      if (
        isNilOrEmpty(dateOfTaxableSupply) ||
        isNilOrEmpty(paymentInfo?.paymentMethod) ||
        isNilOrEmpty(items) ||
        !hasItemWithNonZeroQuantity
      ) {
        return;
      }

      // this is necessary due to difference between calc EP and invoiceApiResponse
      const paymentType =
        paymentInfo.paymentMethod === 'CARD'
          ? 'payment_card'
          : (paymentInfo.paymentMethod.toLowerCase() as PaymentType);

      // A rate converts into the tenant's currency, so it prices nothing once the document is in
      // that currency itself. Only an overriding caller can outrun the form, and a switch that
      // changes the foreign currency is not one of them, so this is the one stale case to catch.
      // The stored `exchangeRateRatio.currency` cannot answer this: it holds a default until a
      // rate lookup succeeds, and the user types the rate by hand when that lookup fails.
      const isExchangeRateRatioApplicable =
        isNil(currencyOverride) || currencyOverride !== tenant.currency;

      const exchangeRatio =
        isExchangeRateRatioEnabled &&
        isExchangeRateRatioApplicable &&
        isNotNilOrEmpty(exchangeRateRatio?.amount) &&
        isNotNilOrEmpty(exchangeRateRatio?.ratio)
          ? {
              ratio: exchangeRateRatio?.ratio?.toString() ?? '',
              amount: parseInt(exchangeRateRatio?.amount ?? ''),
              currency: exchangeRateRatio?.currency ?? '',
            }
          : null;

      // The BE reads `dueDate` as the taxable supply date the VAT rates are resolved for.
      const taxableSupplyDateForCalculation = isNotNil(props.correctedDocumentDateOfTaxableSupply)
        ? props.correctedDocumentDateOfTaxableSupply
        : (dateOfTaxableSupply as string);

      const recalculateInvoiceDocumentBody = {
        vatCheckTotalsDocumentType: props.listItemType,
        dueDate: taxableSupplyDateForCalculation,
        country: tenant.country,
        exchangeRateRatio: exchangeRatio,
        branchId: activeBranchId,
        paymentType,
        currency: recalculationCurrency,
        items,
      };

      const capturedEditVersions = {...props.pricePerUnitEditVersionRef?.current};

      calculateTaxDocument(recalculateInvoiceDocumentBody)
        .unwrap()
        .then((data) => {
          // Items may have been added/removed while this request was in flight -
          // always merge the response against the current form state, never the
          // stale snapshot captured when the request was built (T20-139350).
          const currentInvoiceItems = props.formApi.getValues().invoiceItems;
          // Drop response items that no longer exist locally, so the follow-up
          // summary/VAT totals recalculation doesn't include a removed item either.
          const liveItems = data.items.filter((item) =>
            currentInvoiceItems.some((current) => current.itemId === item.itemId)
          );

          resetInvalidDiscounts({invoiceItems: currentInvoiceItems});

          if (isNil(itemId)) {
            updateAllItems({invoiceItems: currentInvoiceItems, items: liveItems});
            return {...recalculateInvoiceDocumentBody, items: liveItems};
          }

          const updatedItemIndex = currentInvoiceItems.findIndex((item) => item.itemId === itemId);
          const updatedItem = liveItems.find((item) => item.itemId === itemId);

          if (isNil(updatedItem) || isNil(itemId) || !isValidIndex(updatedItemIndex)) {
            return {...recalculateInvoiceDocumentBody, items: liveItems};
          }

          const updateRowProps: UpdateRowType = {
            invoiceItems: currentInvoiceItems,
            updatedItem,
            updatedItemIndex,
            items: liveItems,
            capturedEditVersions,
          };

          if (updatedItem.type === 'discount') {
            updateDiscount(updateRowProps);
          }
          if (updatedItem.type === 'margin') {
            updateMargin(updateRowProps);
          }
          if (updatedItem.type === 'standard') {
            updateStandardItem(updateRowProps);
          }

          updateAllItems({invoiceItems: currentInvoiceItems, items: liveItems});

          return {...recalculateInvoiceDocumentBody, items: liveItems};
        })
        .then(recalculateInvoiceSummary)
        .catch((error: Error) => handleApiError(error));
    },
    // eslint-disable-next-line react/exhaustive-deps -- Muted pre-existing violation
    [
      activeBranchId,
      calculateTaxDocument,
      currency,
      props.correctedDocumentDateOfTaxableSupply,
      props.formApi,
      props.listItemType,
      mapInvoiceItemsForRecalculation,
      recalculateInvoiceSummary,
      resetInvalidDiscounts,
      tenant,
      updateAllItems,
      updateDiscount,
      updateMargin,
      updateStandardItem,
    ]
  );

  useEffect(
    function recalculateOnInitialization() {
      if (props.shouldCallOnInitialization) {
        recalculate();
      }
    },
    [props.shouldCallOnInitialization, recalculate]
  );

  return [recalculate];
};
